import * as React from 'react'
import { Route, Routes } from 'react-router-dom'

import { EmployeesTabsDocument } from 'generated/resources/graphql'
import { EmployeesImporterPageResourceHeaderDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildModuleRoute, buildNavigation } from 'modules/core/lib/react-router'

import competenciesEmployeesRoutes from 'modules/competencies/routes/employees'
import contractsEmployeesRoutes from 'modules/contracts/routes/employees'
import customResourcesEmployeesRoutes from 'modules/custom_resources/routes/employees'
import documentsEmployeesRoutes from 'modules/documents/routes/employees'
import { EmployeesImporterPageResourceHeader } from 'modules/employees/components/EmployeesImporterPageResourceHeader'
import { jobCatalogEmployeesRoutes } from 'modules/job_catalog/routes/employees'
import { orgChartRoutes } from 'modules/org_chart/routes'
import talentEmployeesRoutes from 'modules/performance_analytics/routes/employees'
import performanceEmployeesRoutes from 'modules/performance_v2/routes/employees'
import profileEmployeesRoutes from 'modules/profile/routes/employees'
import tasksEmployeesRoutes from 'modules/tasks/routes/employees'
import { teamsRoutes } from 'modules/teams/routes'
import timePlanningEmployeesRoutes from 'modules/time_planning/routes/employees'
import timeoffEmployeeRoutes from 'modules/timeoff/routes/employees'
import trainingEmployeesRoutes from 'modules/trainings/routes/employees'

import { buildEmployeeMeta } from './employee/buildEmployeeMeta'

const AddEmployeePage = React.lazy(() => import('modules/employees/pages/AddEmployeePage'))
const HeadcountPage = React.lazy(() => import('modules/employees/pages/HeadcountPage'))
const ImportProcessPage = React.lazy(() => import('modules/employees/pages/ImportProcessPage'))
const UniversalImporterEmployeesPage = React.lazy(
  () => import('modules/employees/pages/UniversalImporterEmployeesPage')
)
const UniversalImporterContractsPage = React.lazy(
  () => import('modules/employees/pages/UniversalImporterContractsPage')
)

const OverviewPage = React.lazy(() => import('modules/profile/pages/ProfileOverviewPage'))

const BulkFixContractsPage = React.lazy(
  () => import('modules/employees/pages/BulkFixContractsPage')
)
const CustomTablesImportPage = React.lazy(
  () => import('modules/employees/pages/CustomTablesImportPage')
)
const BulkCreateAdditionalCompensationsPage = React.lazy(
  () => import('modules/employees/pages/BulkCreateAdditionalCompensationsPage')
)
const EditInBulkPage = React.lazy(() => import('modules/employees/pages/EditInBulkPage'))
const EditContractsInBulkPage = React.lazy(
  () => import('modules/employees/pages/EditContractsInBulkPage')
)
const TerminateInBulkPage = React.lazy(() => import('modules/employees/pages/TerminateInBulkPage'))
const FijosPauseInBulkPage = React.lazy(
  () => import('modules/employees/pages/FijosPauseInBulkPage')
)
const FijosActivateInBulkPage = React.lazy(
  () => import('modules/employees/pages/FijosActivateInBulkPage')
)
const EmployeeRootPage = React.lazy(() => import('modules/employees/pages/EmployeeRootPage'))
const EmployeesListPage = React.lazy(() => import('modules/employees/pages/EmployeesListPage'))
const ExportEmployeesModalPage = React.lazy(
  () => import('modules/employees/pages/ExportEmployeesModalPage')
)
const ImportContractsModalPage = React.lazy(
  () => import('modules/employees/pages/ImportContractsModalPage')
)
const ImportContractsModeSelectPage = React.lazy(
  () => import('modules/employees/pages/ImportContractsModeSelectPage')
)
const ImportContractsPage = React.lazy(() => import('modules/employees/pages/ImportContractsPage'))
const ImportCustomTablesModalPage = React.lazy(
  () => import('modules/employees/pages/ImportCustomTablesModalPage')
)
const ImportEmployeesModalPage = React.lazy(
  () => import('modules/employees/pages/ImportEmployeesModalPage')
)

const ExportEmployeesTypeSelectionModalPage = React.lazy(
  () => import('modules/employees/pages/ExportEmployeesTypeSelectionModalPage')
)

const ImportDatevLaudsPage = React.lazy(
  () => import('modules/employees/pages/ImportDatevLaudsPage')
)

const ImportEmployeesPage = React.lazy(() => import('modules/employees/pages/ImportEmployeesPage'))
const ImportTimeoffModalPage = React.lazy(
  () => import('modules/employees/pages/ImportTimeoffModalPage')
)
const BulkAddTimeOffModalPage = React.lazy(
  () => import('modules/employees/pages/BulkAddTimeoffModalPage')
)
const PersonalPage = React.lazy(() => import('modules/employees/pages/PersonalPage'))
const ProfilePage = React.lazy(() => import('modules/profile/pages/ProfilePage'))
const RedirectToEmployeeListPage = React.lazy(
  () => import('modules/employees/pages/RedirectToEmployeeListPage')
)
const RootPage = React.lazy(() => import('modules/employees/pages/RootPage'))

export const employeesRoutes = buildNavigation((navigation) => {
  return [
    buildModuleRoute({
      path: navigation.employees.path,
      element: <RootPage />,
      handle: {
        module: 'employees',
        moduleName: () => i18n.t('sidebar.team'),
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: EmployeesTabsDocument,
          }).map(({ data }) => {
            return {
              title: i18n.t('sidebar.team'),
              subNavigation: [
                {
                  id: 'employees',
                  title: i18n.t('employees.title'),
                  href: navigation.employees.list.fullPath(),
                  index: true,
                },
                {
                  id: 'employees.updates',
                  title: i18n.t('employees.updates.title'),
                  href: navigation.employees.employeeUpdates.fullPath(),
                  isVisible: data?.analytics._policies.sidebarSeeAnalyticsInsightsSubLevelItem.can,
                },
                {
                  id: 'teams',
                  title: i18n.t('teams.title'),
                  href: navigation.employees.teams.fullPath(),
                  isVisible: data?.teams._policies.sidebarSeeEmployeesTeamsSubLevelItem.can,
                },
                {
                  id: 'company_chart',
                  title: i18n.t('company_chart.tab'),
                  href: navigation.employees.organizationChart.fullPath(),
                  isVisible: data?.employees._policies.sidebarSeeEmployeesOrgChartSubLevelItem.can,
                },
                {
                  id: 'job_catalog',
                  title: i18n.t('job_catalog.title'),
                  href: navigation.employees.jobCatalog.fullPath(),
                  isVisible:
                    data?.jobCatalog._policies.sidebarSeeEmployeesJobCatalogSubLevelItem.can,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <RedirectToEmployeeListPage />,
        },
        {
          path: navigation.employees.list.path,
          element: <EmployeesListPage />,
          children: [
            {
              path: navigation.employees.list.add_employee.path,
              element: <AddEmployeePage />,
            },
            {
              path: navigation.employees.list.import.path,
              element: <ImportEmployeesModalPage />,
            },
            {
              path: navigation.employees.list.customTablesImport.path,
              element: <ImportCustomTablesModalPage />,
            },
            {
              path: navigation.employees.list.timeoff.path,
              element: <ImportTimeoffModalPage />,
            },
            {
              path: navigation.employees.list.bulkAddTimeOff.path,
              element: <BulkAddTimeOffModalPage />,
            },
            {
              path: navigation.employees.list.export.path,
              element: <ExportEmployeesModalPage />,
            },
            {
              path: navigation.employees.list.exportTypeSelection.path,
              element: <ExportEmployeesTypeSelectionModalPage />,
            },
            {
              path: navigation.employees.list.importContracts.path,
              element: <ImportContractsModalPage />,
            },
            {
              path: navigation.employees.list.importContractsMode.path,
              element: <ImportContractsModeSelectPage />,
            },
          ],
        },
        {
          path: navigation.employees.edit.path,
          element: (
            <Routes>
              <Route path="" element={<EditInBulkPage />} />
            </Routes>
          ),
          handle: {
            meta: () => ({
              title: i18n.t('employees.edit_employees'),
            }),
          },
        },
        {
          path: navigation.employees.editContracts.path,
          element: (
            <Routes>
              <Route path="" element={<EditContractsInBulkPage />} />
            </Routes>
          ),
          handle: {
            meta: () => ({
              title: i18n.t('employees.edit_contracts'),
            }),
          },
        },
        {
          path: navigation.employees.terminateBulk.path,
          element: (
            <Routes>
              <Route path="" element={<TerminateInBulkPage />} />
            </Routes>
          ),
          handle: {
            meta: () => ({
              title: i18n.t('employees.termination.bulk_modal.title', { employees: '' }),
            }),
          },
        },
        {
          path: navigation.employees.fijosPauseBulk.path,
          element: (
            <Routes>
              <Route path="" element={<FijosPauseInBulkPage />} />
            </Routes>
          ),
          handle: {
            meta: () => ({
              title: i18n.t('employees.pause.bulk_page.title'),
            }),
          },
        },
        {
          path: navigation.employees.fijosActivateBulk.path,
          element: (
            <Routes>
              <Route path="" element={<FijosActivateInBulkPage />} />
            </Routes>
          ),
          handle: {
            meta: () => ({
              title: i18n.t('employees.activate.bulk_page.title'),
            }),
          },
        },
        {
          path: navigation.employees.additionalCompensations.path,
          children: [
            {
              path: navigation.employees.additionalCompensations.create.path,
              element: <BulkCreateAdditionalCompensationsPage />,
              handle: {
                resource: true,
                meta: () => ({
                  title: i18n.t('employees.bulk_edit.additional_compensations'),
                  headerComponent: null,
                }),
              },
            },
          ],
        },
        {
          path: navigation.employees.bulkFixContracts.path,
          element: <BulkFixContractsPage />,
          handle: {
            meta: () => ({
              title: '',
              unsafeDisableNewLayout: true,
            }),
          },
        },
        {
          path: navigation.employees.importEmployees.fullPath({
            legal_entity_id: ':legal_entity_id',
          }),
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterEmployeesPage />} />
            </Routes>
          ),
          handle: {
            resource: true,
            meta: ({ params, watchQuery }) => {
              return watchQuery({
                query: EmployeesImporterPageResourceHeaderDocument,
                variables: {
                  legalEntityId: Number(params?.legal_entity_id ?? 0),
                },
              }).map(({ data }) => {
                const title = data.companies.legalEntity?.legalName ?? ''
                const country = data.companies.legalEntity?.country ?? ''
                return {
                  title: title,
                  headerComponent: (
                    <EmployeesImporterPageResourceHeader
                      title={title}
                      action="create"
                      country={country}
                    />
                  ),
                }
              })
            },
          },
        },
        {
          path: navigation.employees.importContracts.fullPath({
            legal_entity_id: ':legal_entity_id',
          }),
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterContractsPage />} />
            </Routes>
          ),
          handle: {
            resource: true,
            meta: ({ params, watchQuery }) => {
              return watchQuery({
                query: EmployeesImporterPageResourceHeaderDocument,
                variables: {
                  legalEntityId: Number(params?.legal_entity_id ?? 0),
                },
              }).map(({ data }) => {
                const title = data.companies.legalEntity?.legalName ?? ''
                const country = data.companies.legalEntity?.country ?? ''
                return {
                  title: title,
                  headerComponent: (
                    <EmployeesImporterPageResourceHeader
                      title={title}
                      action="create"
                      country={country}
                    />
                  ),
                }
              })
            },
          },
        },
        {
          path: navigation.employees.importProcess.path,
          element: <ImportProcessPage />,
          handle: {
            meta: () => ({
              title: i18n.t('importer_process.title'),
            }),
          },
        },
        {
          path: navigation.employees.import.path,
          element: <ImportEmployeesPage />,
          handle: {
            meta() {
              return {
                title: i18n.t('common.import'),
              }
            },
          },
        },
        {
          path: navigation.employees.import.datev.fullPath({
            employee_import_id: ':employee_import_id',
          }),
          element: <ImportDatevLaudsPage />,
          handle: {
            resource: true,
            meta() {
              return {
                title: i18n.t('common.import'),
              }
            },
          },
        },

        {
          path: navigation.employees.contractImport.path,
          element: <ImportContractsPage />,
          handle: {
            meta() {
              return {
                title: i18n.t('common.import'),
              }
            },
          },
        },
        {
          path: navigation.employees.customTablesImport.path,
          element: <CustomTablesImportPage />,
          handle: {
            meta() {
              return {
                title: i18n.t('common.import'),
              }
            },
          },
        },
        {
          path: navigation.employees.employeeUpdates.path,
          element: <HeadcountPage />,
          handle: {
            meta() {
              return {
                title: i18n.t('employees.updates.title'),
              }
            },
          },
        },
        {
          path: navigation.employees.id.path,
          element: <EmployeeRootPage />,
          handle: {
            resource: true,
            meta(context) {
              return buildEmployeeMeta(context, navigation)
            },
          },
          children: [
            ...profileEmployeesRoutes(navigation),
            ...documentsEmployeesRoutes(navigation),
            ...contractsEmployeesRoutes(navigation),
            {
              path: navigation.employees.id.personal.path,
              element: <PersonalPage />,
            },
            {
              path: navigation.employees.id.workDetails.path,
              element: <ProfilePage />,
            },
            {
              path: navigation.profile.overview.path,
              element: <OverviewPage />,
            },
            ...tasksEmployeesRoutes(navigation),
            ...timeoffEmployeeRoutes(navigation),
            ...customResourcesEmployeesRoutes(navigation),
            ...timePlanningEmployeesRoutes(navigation),
            ...competenciesEmployeesRoutes(navigation),
            ...trainingEmployeesRoutes(navigation),
            ...performanceEmployeesRoutes(navigation),
            ...talentEmployeesRoutes(navigation),
          ],
        },
        ...jobCatalogEmployeesRoutes(navigation),
        ...orgChartRoutes(navigation),
        ...teamsRoutes(navigation),
        // TODO: those routes should follow the same pattern as other modules.
        //       we should create a `employees` file inside `<module>/public/routes`.
        // ...orgChartRoutes(navigation),
        // ...peopleChartEmployeesRoutes(navigation),
        // ...teamEmployeesRoutes(navigation),
      ],
    }),
  ]
})
